import { motion } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { FadeUp } from "@/components/Motion";
import BeeTileBackdrop from "@/components/BeeTileBackdrop";
import { User, Mountain, Sparkles, Hourglass, Droplet, Hexagon } from "lucide-react";
import { QualitaetLogo } from "@/components/QualitaetLogo";

export default function BeyondNature() {
  const { lang } = useI18n();

  const content = {
    de: {
      eyebrow: "Wir imkern mit Leidenschaft",
      title: "BEYOND NATURE",
      items: [
        { icon: User, label: "Direkt vom Imker" },
        { icon: Mountain, label: "Aus den Dolomiten" },
        { icon: Sparkles, label: "Keine Zusätze" },
        { icon: Hourglass, label: "Jährlich limitiert" },
        { icon: Droplet, label: "Purer Geschmack" },
        { icon: Hexagon, label: "Echtes Handwerk" },
      ],
    },
    en: {
      eyebrow: "Beekeeping with passion",
      title: "BEYOND NATURE",
      items: [
        { icon: User, label: "Direct from the Beekeeper" },
        { icon: Mountain, label: "From the Dolomites" },
        { icon: Sparkles, label: "No Additives" },
        { icon: Hourglass, label: "Yearly Limited" },
        { icon: Droplet, label: "Pure Taste" },
        { icon: Hexagon, label: "True Craft" },
      ],
    },
    it: {
      eyebrow: "Apicoltura con passione",
      title: "BEYOND NATURE",
      items: [
        { icon: User, label: "Direttamente dall'Apicoltore" },
        { icon: Mountain, label: "Dalle Dolomiti" },
        { icon: Sparkles, label: "Nessun Additivo" },
        { icon: Hourglass, label: "Limitato Annualmente" },
        { icon: Droplet, label: "Sapore Puro" },
        { icon: Hexagon, label: "Vero Artigianato" },
      ],
    },
    ja: {
      eyebrow: "情熱を込めた養蜂",
      title: "BEYOND NATURE",
      items: [
        { icon: User, label: "養蜂家から直接" },
        { icon: Mountain, label: "ドロミテ産" },
        { icon: Sparkles, label: "無添加" },
        { icon: Hourglass, label: "年間限定" },
        { icon: Droplet, label: "純粋な味わい" },
        { icon: Hexagon, label: "本物の手仕事" },
      ],
    },
    zh: {
      eyebrow: "满怀热情的养蜂事业",
      title: "BEYOND NATURE",
      items: [
        { icon: User, label: "蜂农直供" },
        { icon: Mountain, label: "产自多洛米蒂" },
        { icon: Sparkles, label: "无添加" },
        { icon: Hourglass, label: "每年限量" },
        { icon: Droplet, label: "纯粹风味" },
        { icon: Hexagon, label: "真正的手艺" },
      ],
    },
  }[lang];

  return (
    <section className="relative bg-[#0A0A0A] py-32 lg:py-40 border-t border-white/5 overflow-hidden" data-testid="beyond-nature-section">
      {/* Decorative bee-tile backdrop */}
      <BeeTileBackdrop opacity={0.3} />
      <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12">
        <FadeUp className="text-center mb-20 lg:mb-24">
          <p className="font-editorial italic text-cream/70 text-lg lg:text-xl mb-6">
            {content.eyebrow}
          </p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-7xl tracking-[0.18em] text-gold leading-none">
            {content.title}
          </h2>
          <div className="w-16 h-px bg-gold/60 mx-auto mt-10" />
        </FadeUp>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-14 gap-x-4 lg:gap-x-6">
          {content.items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col items-center text-center group"
              data-testid={`beyond-icon-${i}`}
            >
              <div className="w-16 h-16 lg:w-20 lg:h-20 border border-gold/40 flex items-center justify-center mb-6 group-hover:border-gold group-hover:bg-gold/5 transition-all duration-500">
                <item.icon className="w-6 h-6 lg:w-7 lg:h-7 text-gold" strokeWidth={1.2} />
              </div>
              <div className="font-display text-[10px] lg:text-[11px] tracking-[0.28em] text-gold uppercase leading-tight px-2">
                {item.label}
              </div>
            </motion.div>
          ))}
        </div>

        <FadeUp className="mt-20 flex flex-col items-center gap-4" data-testid="beyond-quality-seal">
          <QualitaetLogo size="md" testid="beyond-qualitaet-logo" />
        </FadeUp>
      </div>
    </section>
  );
}
